<template>
  <div class="content-wrapper">
    <h2 class="page-titles">Recipient Lists</h2>

    <!-- Info text here -->
    <div class="info">
      <!-- <p>Map columns to required fields.</p> -->
    </div>

    <!-- File details here -->
    <div class="details-holder">
      <p class="text">File</p>
      <p class="file">
        {{ uploadedData.attributes ? uploadedData.attributes.file_name : '' }}
      </p>
    </div>

    <!-- Required Fields Here -->
    <div class="required-fields">
      <strong>Required Fields</strong>

      <!-- fields here -->
      <div class="fields">
        <div class="field" v-for="field in requiredFields" :key="field">
          <p>{{ handleField(field) }}</p>
          <img
            :src="columnsToMap.includes(field) ? errorIcon : checkIcon"
            :alt="columnsToMap.includes(field) ? 'Error-Icon' : 'Check-Icon'"
          />
        </div>
      </div>
    </div>

    <!-- Table Here -->
    <div class="table-holder">
      <table>
        <!-- Table Head Here -->
        <thead>
          <tr>
            <td
              v-for="(header, index) in headers"
              :key="index"
              :class="{
                mapped: header.field.length,
                skipped: header.isSkipped
              }"
            >
              <div class="map-holder">
                <div class="recipients-list-select" v-if="!header.isSkipped">
                  <v-select
                    class="glass recipient"
                    :placeholder="!header.field ? 'Unmapped' : ''"
                    :value="header.field | removeOptional"
                    :options="columnsToMap"
                    :clearable="false"
                    :searchable="false"
                    @input="handleMapColumn(index, $event)"
                  >
                    <template #open-indicator="{ attributes }">
                      <span v-bind="attributes">
                        <ToggleIcon />
                      </span>
                    </template>
                  </v-select>
                </div>

                <div v-else class="skipped">Skipped</div>
              </div>

              <div class="button-holder">
                <button
                  class="skip"
                  :class="{ revert: header.isSkipped }"
                  @click="handleSkip(index)"
                >
                  {{ header.isSkipped ? 'Revert' : 'Skip' }}
                </button>
              </div>
            </td>
          </tr>
        </thead>

        <!-- Body Here -->
        <tbody>
          <tr v-for="(recipient, i) in recipients" :key="i">
            <td v-for="(value, index) in recipients[0].length" :key="index">
              {{ recipient[index] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Record region -->
    <div class="record-region">
      <div class="checkbox-region">
        <label class="checkbox-container">
          <p>Ignore first row of spreadsheet</p>
          <input type="checkbox" v-model="checked" />
          <span class="checkmark "></span>
        </label>
      </div>

      <p>
        <strong>{{ totalRecords }}</strong>
        records were uploaded.
      </p>
    </div>

    <!-- Error Display Region -->
    <div>
      <ErrorDisplay />
    </div>

    <!-- Buttons -->
    <div class="button-region">
      <UploadButton
        :isWhiteVariant="true"
        text="Start over"
        @import="handleImport"
        customStyles="padding: 0 1.25rem; font-weight: normal; font-size: 0.813rem"
      />

      <button
        class="button-gemnote button-gemnote--small"
        :class="{
          'button-gemnote--disabled': disabledButton
        }"
        :disabled="disabledButton"
        @click="handlePreview"
      >
        <span>Next</span>
      </button>
    </div>
  </div>
</template>

<script>
import { layouts } from '../../helpers'
import ToggleIcon from './Icons/ToggleIcon'
import ErrorDisplay from './ErrorDisplay'
import UploadButton from './UploadButton'
import { mapState } from 'vuex'
import { progressStatus } from '../../helpers'

export default {
  name: 'MapColumns',
  components: {
    UploadButton,
    ToggleIcon,
    ErrorDisplay
  },

  filters: {
    removeOptional(value) {
      return value.includes('optional')
        ? value.replace('(optional)', '').replace('(Optional)', '')
        : value
    }
  },

  watch: {
    headers: {
      handler() {
        this.headers.forEach((header, index) => {
          if (header.isSkipped) {
            // find all the td elements in tbody in the skipped column and add the class skippped-column
            const tds = document.querySelectorAll(
              `.table-holder table tbody tr td:nth-child(${index + 1})`
            )
            tds.forEach(td => {
              td.classList.add('skippped-column')
            })
          } else {
            // remove skippped-column class from all the td elements in tbody in the skipped column
            const tds = document.querySelectorAll(
              `.table-holder table tbody tr td:nth-child(${index + 1})`
            )
            tds.forEach(td => {
              td.classList.remove('skippped-column')
            })
          }
        })
      },
      deep: true
    },

    recipients: {
      handler() {
        this.setHeaders()
      }
    }
  },

  data: () => ({
    checked: true,
    headers: [],
    requiredFields: [],
    checkIcon: require('../../assets/check.svg'),
    errorIcon: require('../../assets/error.svg')
  }),

  computed: {
    ...mapState({
      uploadedData: state => state.recipient.recipientList,
      loading: state => state.recipient.loadRecipientList,
      errors: state => state.recipient.recipientErrors
    }),

    loadStatus() {
      return progressStatus(this.loading)
    },
    totalRecords() {
      return this.uploadedData && this.uploadedData.attributes
        ? this.uploadedData.attributes.total_records
        : ''
    },
    recipients() {
      return this.uploadedData && this.uploadedData.attributes
        ? this.uploadedData.attributes.data
        : []
    },
    columnsToMap() {
      return this.uploadedData && this.uploadedData.attributes
        ? Object.values(this.uploadedData.attributes.columns_to_map).filter(
            header => {
              return !this.headers.some(h => h.field === header)
            }
          )
        : []
    },
    isMapped() {
      const mapped = this.requiredFields.every(header =>
        this.headers.some(h => h.field === header)
      )
      return mapped
    },
    disabledButton() {
      return !this.isMapped || !!Object.keys(this.errors).length
    }
  },

  created() {
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.$store.dispatch('recipient/loadRecipientList')
    this.setRequiredFields()
    this.setHeaders()
  },

  methods: {
    setHeaders() {
      const headers = []
      const recipients =
        this.recipients &&
        Array.isArray(this.recipients) &&
        this.recipients.length &&
        this.recipients[0].length

      for (let i = 0; i < recipients; i++) {
        headers.push({
          field: '',
          isSkipped: false
        })
      }
      this.$set(this, 'headers', headers)
      this.autoMapColumns()
    },
    autoMapColumns() {
      const firstRow =
        (this.recipients &&
          Array.isArray(this.recipients) &&
          this.recipients.length &&
          this.recipients[0]) ||
        []

      // go through firstRow and check for instances where the data includes a field that is in colums to map
      firstRow.forEach((value, index) => {
        if (value) {
          value = this.checkValue(value.toLowerCase())

          const fieldToMap = this.columnsToMap.find(field =>
            field.toLowerCase().includes(value.toLowerCase())
          )

          if (fieldToMap) {
            this.handleMapColumn(index, fieldToMap)
          }
        }
      })
    },

    checkValue(value) {
      let data = value

      if (value.includes('name')) {
        data = 'Name'
      }

      if (value.includes('phone')) {
        data = 'Phone'
      }

      if (value.includes('state')) {
        data = 'State'
      }

      if (value.includes('address') && value.includes('one')) {
        data = 'Address Line 1'
      }

      if (value.includes('zip') && value.includes('code')) {
        data = 'Zip Code'
      }

      return data
    },

    setRequiredFields() {
      this.requiredFields = this.columnsToMap.filter(
        header => !header.toLowerCase().includes('optional')
      )
    },

    handleMapColumn(index, field) {
      this.headers[index].field = field
    },

    handleSkip(index) {
      this.headers[index].isSkipped = !this.headers[index].isSkipped
      this.headers[index].field = ''
    },
    handleField(field) {
      switch (field) {
        case 'Full Name':
          return 'Name'

        case 'Phone #':
          return 'Phone'

        case 'Zip Code':
          return 'Postal Code'

        default:
          return field
      }
    },

    handleImport(file) {
      const id = this.uploadedData && this.uploadedData.id
      this.$store.dispatch('recipient/handleDeleteRecipientList', parseInt(id))
      this.$store.dispatch('recipient/handleImportRecipients', file)
    },
    handlePreview() {
      const formData = new FormData()
      this.headers.forEach(header => {
        let key = Object.keys(this.uploadedData.attributes.columns_to_map).find(
          k => {
            return (
              this.uploadedData.attributes.columns_to_map[k] == header.field
            )
          }
        )

        formData.append('cols_name[]', key)
      })

      formData.append('include_headers', this.checked ? 'yes' : 'no')
      this.$store.dispatch('recipient/previewRecipients', {
        payload: formData,
        checked: this.checked
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.info {
  display: flex;
  align-items: center;
  margin-top: 2.5rem;
}

.details-holder {
  display: flex;
  align-items: center;
  background: inherit;
  border: 1px solid #000000;
  margin: 1.5rem 0;
  height: 3.625rem;
  width: 45%;
  padding: 1.25rem 2rem;

  p {
    font-size: 0.875rem;

    &:nth-child(1) {
      font-weight: bold;
    }

    &:nth-child(2) {
      margin-left: 2rem;
    }
  }
}

.required-fields {
  width: 45%;
  border: 1px solid;
  padding: 1.25rem 2rem;
  text-align: left;
  margin-top: 2.5rem;
  margin-bottom: 4rem;
  color: $main-6;

  .fields {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-top: 1.75rem;
    /* width: 40%; */
    .field {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      width: 40%;

      p {
        font-size: 0.875rem;
      }

      img {
        margin-left: auto;
        transition: all 3s ease-in-out;
      }
    }
  }
}
.table-holder {
  margin-top: 2rem;
  overflow-x: scroll;
  overflow-y: hidden;
  height: 100%;

  &::-webkit-scrollbar {
    width: 10px;
    height: 4px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #979797;

    &:hover {
      background: #555;
    }
  }

  table {
    width: 100%;
    border-spacing: 0px;
    border-collapse: separate;

    thead > tr > td {
      padding: 0.2rem 1rem;
      font-size: 0.875rem;
      font-weight: bold;
      text-align: left;
      border-bottom: 2px solid #979797;
      background: #dbbd7b;
      border: 1px solid #000000;
      height: 105px;
      border-left: none;

      &:first-child {
        border-left: 1px solid #000000;
      }

      &.mapped {
        background: #dfdad2;
      }

      &.skipped {
        background: #dfdad2;
      }
      .map-holder {
        width: 184px;
        .recipients-list-select {
          flex-basis: 45%;
          display: flex;
          flex-direction: column;
          .glass {
            width: 90%;
            border-bottom: 1px solid;
            height: 2.75rem;
          }
        }
      }

      .button-holder {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 3.625rem;

        button {
          cursor: pointer;
          color: $main-6;
          font-family: $inter;
          background: inherit;
          &.skip {
            font-size: 0.75rem;
            border: none;
            text-decoration: underline;
            font-weight: bold;
          }
          &.revert {
            font-weight: normal;
            text-decoration: none;
            border: 1px solid $main-6;
            font-size: 0.625rem;
            width: 4.75rem;
            height: 1.875rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 40px;
          }
        }
      }
    }

    tbody > tr > td {
      text-align: left;
      padding: 1.25rem 1rem;
      font-size: 0.813rem;
      border-bottom: 1px solid #979797;
      &.skipped {
        opacity: 0.4;
      }
    }
  }
}
.record-region {
  text-align: left;
  padding-top: 0.75rem;
  display: flex;
  align-items: center;
  .checkbox-region {
    margin-right: auto;
    .checkbox-container {
      p {
        margin-left: 25px;
      }
      .checkmark {
        height: 16px;
        width: 16px;

        &:after {
          left: 4px;
          top: 2px;
        }
      }
    }
  }

  p {
    font-size: 0.75rem;
  }
}

.button-region {
  display: flex;
  align-items: center;
  justify-content: end;
  margin: 4.5rem 0;

  button {
    width: 143px;
    display: flex;
    align-items: center;
    height: 3.25rem;

    &:nth-child(2) {
      height: 2.5rem;
      width: auto;
      padding: 0 1.75rem;
      font-weight: normal;
      font-size: 0.813rem;
      margin-left: 1.25rem;
    }
  }
}

div.skipped {
  height: 2.75rem;
  display: flex;
  align-items: center;
}

.skippped-column {
  color: #c5c7c8;
}
</style>
